import React, { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import {
  InviteInfoAccessMode,
  InviteInfoSendMode,
  TestDTO,
  TestSessionSummaryDTO,
  useGetTestSession
} from '@/api/generated';
import CameraIcon from '@/assets/icons/camera.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';
import XSmallIcon from '@/assets/icons/x-small.svg?react';
import EvaluationChip from '@/components/EvaluationChip';
import HeaderProperty from '@/components/HeaderProperty';
import Loader from '@/components/Loader/Loader';
import TestSessionButtons from '@/components/TestSessionButtons';
import TestSessionProgressIcon from '@/components/TestSessionProgressIcon';
import TestStepper from '@/components/TestSessionStepper/TestSessionStepper';
import { AI_ANALYSIS_STATUSES } from '@/containers/Process/options';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';

import { Gallery } from './Gallery';

type TestDTOWithSessionSummary = TestDTO & TestSessionSummaryDTO;

const TestSession = ({ testSessionId }: { testSessionId: string | undefined }) => {
  const { palette, spacing } = useTheme();
  const { t } = useTranslation(['tests', 'common']);
  const { formatDatetime } = useFormattedDatetime();
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const { data: testSession, isLoading } = useGetTestSession(testSessionId!, {
    query: { enabled: !!testSessionId }
  });

  const isWebcamEnabled = Boolean(testSession?.settings?.webcamSettings?.isEnabled);
  const isAiAnalysisEnabled = Boolean(testSession?.settings?.webcamSettings?.isAiAnalysisEnabled);
  const aiAnalysisStatus = testSession?.aiAnalysisStatus;

  const statusConfig = useMemo(() => AI_ANALYSIS_STATUSES.find(s => s.value === aiAnalysisStatus), [aiAnalysisStatus]);
  const aiStatusIcon = statusConfig?.Icon?.();

  if (isLoading || !testSession) return <Loader />;

  const testSessionSummary: Partial<TestDTOWithSessionSummary> = {
    ...testSession,
    createdAt: testSession?.statusUpdates?.REGISTERED
  };

  return (
    <Box
      sx={{
        paddingX: spacing(3),
        paddingY: spacing(2),
        backgroundColor: palette.primary[50],
        textAlign: 'start'
      }}
    >
      <Stack
        component="dt"
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing(1.25)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TestSessionProgressIcon testSession={testSessionSummary as TestDTOWithSessionSummary} />
          <Typography
            variant="body1"
            sx={{
              color: palette.text.primary,
              marginInlineStart: spacing(1)
            }}
          >
            {testSession?.test?.name}
          </Typography>
          <EvaluationChip evaluation={testSession?.evaluation} />
          {isWebcamEnabled && <CameraIcon />}
          {isAiAnalysisEnabled && aiAnalysisStatus && aiStatusIcon}
        </Box>
        <TestSessionButtons testSession={testSession} />
      </Stack>
      <TestStepper
        testSession={testSessionSummary as TestDTOWithSessionSummary}
        sx={{
          padding: spacing(1),
          marginBottom: spacing(1),
          borderRadius: spacing(0.5),
          backgroundColor: palette.background.default
        }}
      />
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          rowGap: spacing(2),
          padding: spacing(1),
          backgroundColor: palette.background.default,
          borderRadius: spacing(0.5),
          margin: 0
        }}
      >
        <TestInfoProperty label={t('fieldNames.camera')}>
          <ValueIcon value={isWebcamEnabled} />
        </TestInfoProperty>
        <TestInfoProperty label={t('fieldNames.proctoring')}>
          {!isWebcamEnabled && <ValueIcon value={false} />}
          {isWebcamEnabled && <ProctoringValue isAiAnalysisEnabled={isAiAnalysisEnabled} statusConfig={statusConfig} />}
        </TestInfoProperty>
        <TestInfoProperty label={t('fieldNames.lastUpdate')}>{formatDatetime(testSession?.updatedAt)}</TestInfoProperty>

        <Box sx={{ gridColumn: 'span 3' }}>
          <Accordion
            expanded={accordionExpanded}
            onChange={() => setAccordionExpanded(prev => !prev)}
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              '&:before': { display: 'none' },
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              // Accordion renders [Context.Provider(summary), Collapse(details)]; order so details appear above summary
              '& > :first-of-type': { order: 2 },
              '& > .MuiCollapse-root': { order: 1 }
            }}
          >
            <AccordionSummary
              id="test-session-info-summary"
              aria-controls="test-session-info-details"
              sx={{ minHeight: 0, padding: 0 }}
            >
              <Typography variant="body2" component="span" color="primary.main">
                {t(`layouts.testSessionInfo.${accordionExpanded ? 'seeLess' : 'seeMore'}`)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: 0,
                paddingBottom: spacing(2),
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                rowGap: spacing(2)
              }}
            >
              <TestInfoProperty label={t('fieldNames.retest')}>
                <ValueIcon value={testSession!.isRetest ?? false} />
              </TestInfoProperty>
              <TestInfoProperty label={t('fieldNames.sentBy')}>
                {testSession?.invite?.sendMode != null
                  ? t(`layouts.testSessionInfo.sendBy.${sendModeToTranslationKey(testSession.invite.sendMode)}`)
                  : '—'}
              </TestInfoProperty>
              <TestInfoProperty label={t('fieldNames.testLang')}>
                {testSession?.locale ? t(`common.languages.${testSession.locale}`, { ns: 'common' }) : '—'}
              </TestInfoProperty>
              <TestInfoProperty label={t('fieldNames.invitationSent')}>
                <ValueIcon value={Boolean(testSession?.invite?.createdAt)} />
              </TestInfoProperty>
              <TestInfoProperty label={t('fieldNames.accessMode')}>
                {testSession?.invite?.accessMode != null
                  ? t(`layouts.testSessionInfo.accessMode.${accessModeToTranslationKey(testSession.invite.accessMode)}`)
                  : '—'}
              </TestInfoProperty>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
      {isWebcamEnabled && (
        <Box id="gallery" sx={{ marginTop: spacing(2) }}>
          <Typography variant="body2" component="span" color="primary.main">
            {t('layouts.photoGallery')}
          </Typography>
          <Divider sx={{ marginY: spacing(2) }} />
          <Gallery testSessionId={testSessionId!} />
        </Box>
      )}
    </Box>
  );
};

export default TestSession;

const TestInfoProperty = ({ label, children }: { label: string; children?: ReactNode }) => (
  <HeaderProperty direction="column" sx={{ margin: 0 }} label={label}>
    {children}
  </HeaderProperty>
);

const ValueIcon = ({ value }: { value: boolean | null | undefined }) =>
  value ? <CheckIcon width={16} height={16} /> : <XSmallIcon width={16} height={16} />;

const ProctoringValue = ({
  isAiAnalysisEnabled,
  statusConfig
}: {
  isAiAnalysisEnabled: boolean | null | undefined;
  statusConfig: (typeof AI_ANALYSIS_STATUSES)[number] | undefined;
}) => {
  const StatusIcon = statusConfig?.Icon;

  return isAiAnalysisEnabled ? (
    <Stack direction="row" alignItems="center" gap={1}>
      <CheckIcon width={16} height={16} />
      {StatusIcon && <StatusIcon />}
    </Stack>
  ) : (
    <XSmallIcon width={16} height={16} />
  );
};

const sendModeToTranslationKey = (sendMode: InviteInfoSendMode): string => {
  const keyMap: Partial<Record<InviteInfoSendMode, string>> = {
    [InviteInfoSendMode.MANUAL]: 'manual',
    [InviteInfoSendMode.EMAIL]: 'email'
    // [InviteInfoSendMode.TEXT_MESSAGE]: 'textMessage',
    // [InviteInfoSendMode.WHATSAPP]: 'whatsapp'
  };

  // eslint-disable-next-line i18next/no-literal-string
  return keyMap[sendMode] ?? 'manual';
};

const accessModeToTranslationKey = (accessMode: InviteInfoAccessMode): string => {
  const keyMap: Record<InviteInfoAccessMode, string> = {
    [InviteInfoAccessMode.PASSWORD]: 'password',
    [InviteInfoAccessMode.UNIQUE_LINK]: 'uniqueLink',
    [InviteInfoAccessMode.PUBLIC_LINK]: 'publicLink'
  };

  // eslint-disable-next-line i18next/no-literal-string
  return keyMap[accessMode] ?? 'password';
};
